/**
 * Inventory Service
 * Handles all inventory management API operations
 * 
 * @module services/inventoryService
 * @requires services/api/BaseService
 */

import { BaseService } from './api';

/**
 * Inventory Service Class
 * Provides methods for stock management, alerts, movements, and analytics
 * 
 * @class InventoryService
 * @extends BaseService
 * 
 * @example
 * import inventoryService from './services/inventoryService';
 * 
 * // Get all items
 * const items = await inventoryService.getAll({ category: 'electronics' });
 * 
 * // Update stock
 * await inventoryService.updateStock('item-123', { quantity: 50 });
 */
class InventoryService extends BaseService {
  constructor() {
    super('/inventory');
  }

  /**
   * Get all inventory items with pagination and filters
   * 
   * @param {Object} params - Query parameters
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=20] - Items per page
   * @param {string} [params.category] - Filter by category
   * @param {string} [params.supplier] - Filter by supplier
   * @param {string} [params.location] - Filter by location
   * @param {string} [params.status] - Filter by status (in_stock, low_stock, out_of_stock)
   * @param {string} [params.search] - Search query
   * @param {string} [params.sortBy] - Sort field
   * @param {string} [params.sortOrder] - Sort order (asc, desc)
   * @returns {Promise<{data: Array, total: number, page: number, limit: number}>}
   */
  async getAll(params = {}) {
    return this.get('', { params });
  }

  /**
   * Get inventory item by ID
   * 
   * @param {string} itemId - Item ID
   * @returns {Promise<Object>} Item details
   */
  async getById(itemId) {
    return this.get(`/${itemId}`);
  }

  /**
   * Create new inventory item
   * 
   * @param {Object} itemData - Item data
   * @param {string} itemData.name - Item name
   * @param {string} itemData.sku - Stock keeping unit
   * @param {string} itemData.category - Category
   * @param {number} itemData.quantity - Initial quantity
   * @param {number} itemData.reorderLevel - Minimum stock level
   * @param {number} itemData.unitPrice - Unit price
   * @param {string} [itemData.supplier] - Supplier
   * @param {string} [itemData.location] - Storage location
   * @param {string} [itemData.description] - Description
   * @returns {Promise<Object>} Created item
   */
  async create(itemData) {
    return this.post('', itemData);
  }

  /**
   * Update inventory item
   * 
   * @param {string} itemId - Item ID
   * @param {Object} itemData - Updated item data
   * @returns {Promise<Object>} Updated item
   */
  async update(itemId, itemData) {
    return this.put(`/${itemId}`, itemData);
  }

  /**
   * Delete inventory item
   * 
   * @param {string} itemId - Item ID
   * @returns {Promise<void>}
   */
  async delete(itemId) {
    return this.delete(`/${itemId}`);
  }

  /**
   * Search inventory items with cancellation
   * 
   * @param {string} query - Search query
   * @param {Object} [options={}] - Search options
   * @param {string} [options.category] - Filter by category
   * @param {number} [options.limit=10] - Max results
   * @returns {Promise<Array>} Matching items
   */
  async search(query, options = {}) {
    return this.getWithCancel('/search', 'inventory-search', {
      params: { q: query, ...options }
    });
  }

  /**
   * Update stock quantity
   * 
   * @param {string} itemId - Item ID
   * @param {Object} stockData - Stock update data
   * @param {number} stockData.quantity - New quantity
   * @param {string} [stockData.reason] - Reason for update
   * @returns {Promise<Object>} Updated item
   */
  async updateStock(itemId, stockData) {
    return this.patch(`/${itemId}/stock`, stockData);
  }

  /**
   * Adjust stock (add or remove)
   * 
   * @param {string} itemId - Item ID
   * @param {Object} adjustmentData - Adjustment data
   * @param {string} adjustmentData.type - Adjustment type (add, remove)
   * @param {number} adjustmentData.quantity - Quantity to adjust
   * @param {string} adjustmentData.reason - Reason for adjustment
   * @param {string} [adjustmentData.reference] - Reference document
   * @returns {Promise<Object>} Updated item with movement record
   */
  async adjustStock(itemId, adjustmentData) {
    return this.post(`/${itemId}/adjust`, adjustmentData);
  }

  /**
   * Transfer stock between locations
   * 
   * @param {string} itemId - Item ID
   * @param {Object} transferData - Transfer data
   * @param {string} transferData.fromLocation - Source location
   * @param {string} transferData.toLocation - Destination location
   * @param {number} transferData.quantity - Quantity to transfer
   * @param {string} [transferData.notes] - Transfer notes
   * @returns {Promise<Object>} Transfer record
   */
  async transferStock(itemId, transferData) {
    return this.post(`/${itemId}/transfer`, transferData);
  }

  /**
   * Get low stock items (below reorder level)
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.category] - Filter by category
   * @param {string} [params.location] - Filter by location
   * @returns {Promise<Array>} Low stock items
   */
  async getLowStock(params = {}) {
    return this.get('/low-stock', { params });
  }

  /**
   * Get out of stock items
   * 
   * @param {Object} [params={}] - Query parameters
   * @returns {Promise<Array>} Out of stock items
   */
  async getOutOfStock(params = {}) {
    return this.get('/out-of-stock', { params });
  }

  /**
   * Get stock alerts
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.severity] - Filter by severity (critical, warning)
   * @param {boolean} [params.unread] - Only unread alerts
   * @returns {Promise<Array>} Stock alerts
   */
  async getAlerts(params = {}) {
    return this.get('/alerts', { params });
  }

  /**
   * Mark alert as read
   * 
   * @param {string} alertId - Alert ID
   * @returns {Promise<void>}
   */
  async markAlertRead(alertId) {
    return this.patch(`/alerts/${alertId}/read`);
  }

  /**
   * Get inventory movements/history
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.itemId] - Filter by item
   * @param {string} [params.type] - Filter by type (purchase, sale, adjustment, transfer)
   * @param {string} [params.startDate] - Start date
   * @param {string} [params.endDate] - End date
   * @param {number} [params.page=1] - Page number
   * @param {number} [params.limit=50] - Items per page
   * @returns {Promise<{data: Array, total: number}>}
   */
  async getMovements(params = {}) {
    return this.get('/movements', { params });
  }

  /**
   * Get movements for specific item
   * 
   * @param {string} itemId - Item ID
   * @param {Object} [params={}] - Query parameters
   * @returns {Promise<Array>} Movement history
   */
  async getItemMovements(itemId, params = {}) {
    return this.get(`/${itemId}/movements`, { params });
  }

  /**
   * Get items by category
   * 
   * @param {string} category - Category name
   * @param {Object} [params={}] - Additional parameters
   * @returns {Promise<Array>} Items in category
   */
  async getByCategory(category, params = {}) {
    return this.get('/by-category', { params: { category, ...params } });
  }

  /**
   * Get items by supplier
   * 
   * @param {string} supplier - Supplier name
   * @param {Object} [params={}] - Additional parameters
   * @returns {Promise<Array>} Items from supplier
   */
  async getBySupplier(supplier, params = {}) {
    return this.get('/by-supplier', { params: { supplier, ...params } });
  }

  /**
   * Get items by location
   * 
   * @param {string} location - Location name
   * @param {Object} [params={}] - Additional parameters
   * @returns {Promise<Array>} Items at location
   */
  async getByLocation(location, params = {}) {
    return this.get('/by-location', { params: { location, ...params } });
  }

  /**
   * Get inventory analytics
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.period='month'] - Time period (day, week, month, year)
   * @param {string} [params.startDate] - Start date
   * @param {string} [params.endDate] - End date
   * @returns {Promise<Object>} Analytics data
   */
  async getAnalytics(params = {}) {
    return this.get('/analytics', { params });
  }

  /**
   * Get inventory valuation
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.category] - Filter by category
   * @param {string} [params.location] - Filter by location
   * @returns {Promise<Object>} Valuation data
   */
  async getValuation(params = {}) {
    return this.get('/valuation', { params });
  }

  /**
   * Get stock turnover rate
   * 
   * @param {Object} [params={}] - Query parameters
   * @param {string} [params.period='month'] - Time period
   * @returns {Promise<Object>} Turnover rate data
   */
  async getTurnoverRate(params = {}) {
    return this.get('/turnover-rate', { params });
  }

  /**
   * Bulk operations
   * 
   * @param {Object} bulkData - Bulk operation data
   * @param {Array<string>} bulkData.itemIds - Array of item IDs
   * @param {string} bulkData.action - Action (updateCategory, updateLocation, updateSupplier, delete)
   * @param {Object} [bulkData.data] - Additional data for the action
   * @returns {Promise<{success: number, failed: number, results: Array}>}
   */
  async bulkOperation(bulkData) {
    return this.post('/bulk', bulkData);
  }

  /**
   * Import inventory items from file
   * 
   * @param {File} file - CSV or Excel file
   * @param {Object} [options={}] - Import options
   * @param {boolean} [options.updateExisting=false] - Update existing items
   * @param {boolean} [options.skipErrors=true] - Skip rows with errors
   * @returns {Promise<{imported: number, updated: number, failed: number, errors: Array}>}
   */
  async import(file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('options', JSON.stringify(options));
    
    return this.post('/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  /**
   * Export inventory data
   * 
   * @param {Object} [params={}] - Export parameters
   * @param {string} [params.format='csv'] - Export format (csv, xlsx, pdf)
   * @param {Array<string>} [params.fields] - Fields to include
   * @param {Object} [params.filters] - Filters to apply
   * @returns {Promise<Blob>} Export file
   */
  async export(params = {}) {
    return this.get('/export', {
      params,
      responseType: 'blob'
    });
  }

  /**
   * Get inventory statistics
   * 
   * @returns {Promise<Object>} Inventory statistics
   */
  async getStatistics() {
    return this.get('/statistics');
  }

  /**
   * Validate SKU availability
   * 
   * @param {string} sku - SKU to validate
   * @param {string} [excludeItemId] - Item ID to exclude (for updates)
   * @returns {Promise<{available: boolean}>}
   */
  async validateSku(sku, excludeItemId = null) {
    return this.get('/validate-sku', {
      params: { sku, excludeItemId }
    });
  }

  /**
   * Generate reorder suggestions based on sales patterns
   * 
   * @param {Object} [params={}] - Parameters
   * @param {number} [params.daysToAnalyze=30] - Days of history to analyze
   * @param {number} [params.forecastDays=14] - Days to forecast
   * @returns {Promise<Array>} Reorder suggestions
   */
  async getReorderSuggestions(params = {}) {
    return this.get('/reorder-suggestions', { params });
  }
}

// Export singleton instance
const inventoryService = new InventoryService();
export default inventoryService;
